import { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import { useAppContext } from '@/Main';
import { formatUtcDate } from '@/helpers/formatUtcDate';
import { getColor } from '@/helpers/utils';
import type { VisibilityTrendBrandSeries, VisibilityTrendResponse } from '../../types';
import { getHelpDescription } from '../../utils/help/help-content';
import { PANEL_FONT_FAMILY } from '../../utils/panel-font';

const SERIES_COLORS = ['#7c3aed', '#2563eb', '#059669', '#d97706', '#db2777'];

function shiftIsoDay(isoDate: string, deltaDays: number): string {
  const date = new Date(`${isoDate}T12:00:00.000Z`);
  date.setUTCDate(date.getUTCDate() + deltaDays);
  return date.toISOString().slice(0, 10);
}

/** Pad ±1 day so sparse / short ranges don't glue points to chart edges. */
function padTrendForChart(trend: VisibilityTrendResponse): {
  dates: string[];
  brands: VisibilityTrendBrandSeries[];
} {
  const first = trend.dates[0];
  const last = trend.dates[trend.dates.length - 1];
  const dates = [shiftIsoDay(first, -1), ...trend.dates, shiftIsoDay(last, 1)];

  const brands = trend.brands.map(brand => ({
    ...brand,
    scores: [null, ...brand.scores, null],
    positions: [null, ...brand.positions, null],
    aiMentions: brand.aiMentions ? [null, ...brand.aiMentions, null] : brand.aiMentions,
  }));

  return { dates, brands };
}

export function VisibilityTrendChart({
  trend,
}: {
  trend: VisibilityTrendResponse | null;
}) {
  const {
    config: { isDark },
  } = useAppContext() as { config: { isDark: boolean } };

  const option = useMemo(() => {
    if (!trend?.brands.length || !trend.dates.length) return null;

    const pointCount = Math.max(
      ...trend.brands.map(brand => brand.scores.filter(score => score != null).length),
      0,
    );
    // Show chart with a single day of data (dot); empty only when no scores at all.
    if (pointCount < 1) return null;

    const { dates, brands } = padTrendForChart(trend);
    const labels = dates.map(date => formatUtcDate(date, { preset: 'date' }) || date);
    const sparse = pointCount === 1 || dates.length <= 14;
    // Short ranges: keep extra visual margin beyond the padded days.
    const shortRange = dates.length <= 7;

    const axisLabelColor = getColor('gray-700');
    const axisLineColor = getColor('gray-300');
    const splitLineColor = getColor('gray-200');
    const mutedText = getColor('gray-600');
    const tooltipBg = getColor('gray-100');
    const tooltipBorder = getColor('gray-300');
    const tooltipText = getColor('gray-1100');

    return {
      color: SERIES_COLORS,
      tooltip: {
        trigger: 'axis',
        backgroundColor: tooltipBg,
        borderColor: tooltipBorder,
        borderWidth: 1,
        extraCssText: 'direction: rtl; text-align: right;',
        textStyle: { color: tooltipText, fontSize: 12, fontFamily: PANEL_FONT_FAMILY },
        formatter: (
          params: Array<{
            seriesName: string;
            value: number | null;
            axisValue: string;
            dataIndex: number;
            color: string;
          }>,
        ) => {
          if (!params.length) return '';
          const date = params[0].axisValue;
          const rows = params
            .filter(item => item.value != null)
            .map(item => {
              const series = brands.find(brand => brand.name === item.seriesName);
              const position = series?.positions[item.dataIndex];
              const aiMentions = series?.aiMentions?.[item.dataIndex] ?? null;
              return `
                <div style="margin-top:6px">
                  <span style="display:inline-block;width:8px;height:8px;border-radius:999px;background:${item.color};margin-inline-end:6px"></span>
                  <strong>${item.seriesName}</strong><br/>
                  <span style="color:${mutedText};padding-inline-start:14px" title="${getHelpDescription('ai_mentions')}">
                    دیده‌شدن در AI ${item.value}% · منشن در AI ${aiMentions ?? '—'} · میانگین رتبه ${
                      position != null ? `#${position}` : '—'
                    }
                  </span>
                </div>`;
            })
            .join('');
          return `<div style="font-family:${PANEL_FONT_FAMILY};font-size:12px;direction:rtl;text-align:right;color:${tooltipText}"><div style="font-weight:600;margin-bottom:2px">${date}</div>${rows}</div>`;
        },
      },
      legend: { show: false },
      grid: { left: 16, right: 36, top: 12, bottom: 28 },
      xAxis: {
        type: 'category',
        data: labels,
        boundaryGap: shortRange,
        axisLine: { lineStyle: { color: axisLineColor } },
        axisLabel: { color: axisLabelColor, fontSize: 11, fontFamily: PANEL_FONT_FAMILY },
        axisTick: { show: false },
      },
      yAxis: {
        type: 'value',
        position: 'right',
        min: 0,
        max: 100,
        splitLine: { lineStyle: { color: splitLineColor } },
        axisLabel: {
          color: axisLabelColor,
          fontSize: 11,
          fontFamily: PANEL_FONT_FAMILY,
          formatter: '{value}%',
        },
      },
      series: brands.map(brand => ({
        name: brand.name,
        type: 'line',
        smooth: true,
        showSymbol: sparse,
        symbolSize: pointCount === 1 ? 10 : 6,
        lineStyle: { width: 2.5 },
        data: brand.scores,
        connectNulls: false,
      })),
    };
  }, [trend, isDark]);

  if (!option) {
    return (
      <div className="d-flex align-items-center justify-content-center fs--1 text-700" style={{ height: 280 }}>
        پرامپت‌های بیشتری اجرا کنید تا ترند دیده‌شدن را ببینید.
      </div>
    );
  }

  return (
    <div>
      <ReactECharts
        key={isDark ? 'dark' : 'light'}
        option={option}
        style={{ height: 248 }}
        opts={{ renderer: 'svg' }}
      />
      <div className="mt-3 d-flex flex-wrap align-items-center justify-content-center gap-3 px-1">
        {trend!.brands.map((brand, index) => (
          <div
            key={brand.brandId ?? brand.name}
            className="d-flex align-items-center gap-2 fs--2 text-700"
            style={{ maxWidth: '100%' }}
            title={brand.name}
          >
            <span
              className="flex-shrink-0 rounded-circle"
              style={{
                width: '0.5rem',
                height: '0.5rem',
                backgroundColor: SERIES_COLORS[index % SERIES_COLORS.length],
              }}
            />
            <span>{brand.name}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
